/** Team-Kalender: wer ist wann abwesend (Monatsansicht). */
import { cachedGet } from '../store.js';
import { esc } from '../ui.js';
import type { CalendarItem } from '../../shared/types.js';
import { berlinDate, daysOfMonth, monthLabel, shiftMonth, weekday } from '../../shared/time.js';

interface CalendarResponse {
  users: { id: number; name: string; short: string }[];
  items: CalendarItem[];
  holidays: { date: string; name: string }[];
}

const KIND: Record<CalendarItem['kind'], string> = {
  urlaub: 'Urlaub', krank: 'krank', abwesend: 'abwesend', beantragt: 'Urlaub beantragt',
};

export async function renderCalendar(el: HTMLElement, month: string): Promise<void> {
  el.innerHTML = '<h1>Team-Kalender</h1><p class="muted">Lädt …</p>';
  const r = await cachedGet<CalendarResponse>(`calendar:${month}`, `/calendar?month=${month}`);
  const data = r.data ?? { users: [], items: [], holidays: [] };
  const days = daysOfMonth(month);
  const today = berlinDate(Date.now());
  const hol = new Map(data.holidays.map((h) => [h.date, h.name]));
  // Mitarbeiter, die in den Einträgen auftauchen (auch Admins mit Abwesenheit)
  const people = [...data.users];
  for (const i of data.items) if (!people.some((p) => p.id === i.userId)) people.push({ id: i.userId, name: i.name, short: i.short });

  const lead = (weekday(days[0]) + 6) % 7; // Montag = 0
  const cells: string[] = [];
  for (let i = 0; i < lead; i++) cells.push('<div class="cal-cell empty"></div>');
  for (const d of days) {
    const wd = weekday(d);
    const items = data.items.filter((i) => i.startDate <= d && i.endDate >= d);
    cells.push(`<div class="cal-cell ${wd === 0 || wd === 6 ? 'we' : ''} ${hol.has(d) ? 'hol' : ''} ${d === today ? 'today' : ''}">
      <span class="cal-day">${Number(d.slice(8))}</span>
      ${hol.has(d) ? `<span class="cal-hol">${esc(hol.get(d))}</span>` : ''}
      ${wd === 0 || wd === 6 ? '' : items.map((i) => `<span class="chip k-${i.kind}" title="${esc(i.name)}: ${esc(KIND[i.kind])}">${esc(i.short)}${i.halfDay ? '½' : ''}</span>`).join('')}
    </div>`);
  }

  const list = data.items
    .slice()
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((i) => `<li><span class="chip k-${i.kind}">${esc(i.short)}</span> ${esc(i.name)}: ${esc(KIND[i.kind])},
      ${i.startDate === i.endDate ? esc(i.startDate.slice(8) + '.' + i.startDate.slice(5, 7) + '.') : `${esc(i.startDate.slice(8) + '.' + i.startDate.slice(5, 7) + '.')}–${esc(i.endDate.slice(8) + '.' + i.endDate.slice(5, 7) + '.')}`}${i.halfDay ? ' (halb)' : ''}</li>`)
    .join('');

  el.innerHTML = `
    <div class="month-nav">
      <a class="btn icon" href="#/kalender/${shiftMonth(month, -1)}" aria-label="Vormonat">‹</a>
      <h1>${esc(monthLabel(month))}</h1>
      <a class="btn icon" href="#/kalender/${shiftMonth(month, 1)}" aria-label="Folgemonat">›</a>
    </div>
    ${r.offline ? '<div class="alert">Offline – Anzeige aus dem Speicher.</div>' : ''}
    <div class="cal">
      ${['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => `<div class="cal-head">${w}</div>`).join('')}
      ${cells.join('')}
    </div>
    <p class="legend small"><span class="chip k-urlaub">Urlaub</span> <span class="chip k-abwesend">abwesend</span>
      <span class="chip k-krank">krank</span> <span class="chip k-beantragt">beantragt</span></p>
    ${list ? `<ul class="plain">${list}</ul>` : '<p class="muted">Diesen Monat sind alle da.</p>'}
    <p class="muted small">Team: ${people.map((p) => `${esc(p.short)} = ${esc(p.name)}`).join(', ')}</p>
  `;
}
